import type { AxiosResponse } from 'axios';

import Axios from '@/utils/axios';
import { onSessionClear } from '@/utils/sessionReset';

import { todayJakarta } from '../format';
import type {
  BulkPickingResult,
  BulkFulfillmentResult,
  BulkShipResult,
  CancelOutboundOrderResult,
  CreateInboundReceiptPayload,
  CreateOutboundOrderPayload,
  CreateOutboundOrderResult,
  CreatePurchaseOrderPayload,
  UpdateDraftPurchaseOrderPayload,
  UpdateOutboundOrderPayload,
  CreateSkuPayload,
  CreateStoragePayload,
  CreateHubTransferPayload,
  CreateTransferPayload,
  CreateStockOpnamePayload,
  CreateVendorPayload,
  CsvCommitResult,
  CsvDryRunResult,
  CsvFile,
  CsvImportError,
  DestinationConfig,
  DestinationPlace,
  FieldError,
  GeocodeResult,
  GoodsIssueDetail,
  GoodsIssueSource,
  GoodsIssueSourcesQuery,
  GoodsIssueSourceStorage,
  GoodsIssueSourceStoragesQuery,
  GoodsIssueStageSummary,
  GoodsIssueSummaryQuery,
  GoodsIssueSummary,
  GoodsReceiptDetail,
  GoodsReceiptSummary,
  HubClientSnapshot,
  ImportSkusResult,
  ImportVendorsResult,
  InboundRequestSummary,
  ListGoodsIssuesQuery,
  ListGoodsReceiptsQuery,
  ListInboundRequestsQuery,
  ExportOutboundOrdersQuery,
  CityOption,
  ResolveCityResult,
  ListOutboundOrdersQuery,
  ListPurchaseOrdersQuery,
  ListSkusQuery,
  ListStocksQuery,
  ListStoragesPageQuery,
  ListTransfersQuery,
  ListOpnameBatchesQuery,
  ListStockOpnamesQuery,
  OpnameImportResult,
  OpnameBatch,
  StockOpnameDetail,
  StockOpnameFilterQuery,
  StockOpnameStatusSummary,
  StockOpnameSummary,
  ListVendorsQuery,
  OutboundOrder,
  OutboundOrderDetail,
  OutboundOrderShippingLabel,
  OutboundOrderSummary,
  OutboundOrderSummaryQuery,
  OutboundOrderUpload,
  Paginated,
  PurchaseOrder,
  PurchaseOrderDetail,
  PurchaseOrderSummary,
  PurchaseOrderSummaryQuery,
  ShortfallRow,
  SkuRowPayload,
  StockAvailability,
  StockInventoryCsvQuery,
  StockMovement,
  StockMovementType,
  StockPeriodCsvQuery,
  StockSummary,
  StockSummaryQuery,
  StorageCategory,
  StorageStatus,
  StorageType,
  SubmitShippingResult,
  HubTransferDetail,
  HubTransferFilterQuery,
  HubTransferStatusSummary,
  HubTransferSummary,
  ListHubTransfersQuery,
  TransferDetail,
  TransferFilterQuery,
  TransferStatusSummary,
  TransferSummary,
  UpdateGoodsIssueLinesPayload,
  UpdateSkuPayload,
  UpdateVendorPayload,
  UploadedDocument,
  Vendor,
  VendorRowPayload,
  WarehouseSku,
  WarehouseStock,
  WarehouseStorage,
  ExternalIdCheck,
  FulfillmentCarrier,
  SubmitShippingPayload,
  UpdateFulfillmentPayload,
  UpdateStoragePayload,
  CapacityImportResult,
  ListStorageOccupancyQuery,
  SaveStorageLayoutPayload,
  SaveStorageLayoutResult,
  StorageLayout,
  StorageOccupancyBoardLocation,
  StorageOccupancyBoardQuery,
  StorageOccupancyChild,
  StorageOccupancyChildren,
  StorageOccupancyChildrenQuery,
  StorageOccupancyFilterQuery,
  StorageOccupancyPage,
  StorageOccupancyRow,
  WarehouseMapPayload,
  WarehouseMapResult,
  LocationUtilization,
  LocationUtilizationAttention,
  LocationUtilizationPayload,
  LocationUtilizationQuery,
} from '../types';

/**
 * Typed client for `nest-logistic-service` `/v1/warehouses/*` (WMS v2 TRD
 * § API contracts). Every call unwraps the `{ status, message, data }`
 * envelope and every failure is normalised into a {@link WarehouseApiError}
 * so screens can branch on the machine `code` (INSUFFICIENT_STOCK,
 * VALIDATION, CSV_HAS_ERRORS, …) and render the `errors` detail array.
 */

const BASE = '/v1/warehouses';

/* ------------------------------ errors ------------------------------- */

const MACHINE_CODE = /^[A-Z][A-Z0-9_]+$/;

export class WarehouseApiError extends Error {
  /** HTTP status; null for network failures. */
  readonly status: number | null;
  /** Machine code (`INSUFFICIENT_STOCK`, `VALIDATION`, …); null when absent. */
  readonly code: string | null;
  /** Detail array attached by the use case (shortfall, field or row errors). */
  readonly errors: unknown[];

  constructor(
    message: string,
    opts: {
      status?: number | null;
      code?: string | null;
      errors?: unknown[];
    } = {}
  ) {
    super(message);
    this.name = 'WarehouseApiError';
    this.status = opts.status ?? null;
    this.code = opts.code ?? null;
    this.errors = opts.errors ?? [];
  }

  /** `{field, message, lineIndex?}` items (VALIDATION). */
  get fieldErrors(): FieldError[] {
    return this.errors.filter(
      (e): e is FieldError =>
        !!e &&
        typeof e === 'object' &&
        typeof (e as FieldError).message === 'string' &&
        typeof (e as FieldError).field === 'string'
    );
  }

  /** `{sku, requested, available, shortfall}` items (INSUFFICIENT_STOCK). */
  get shortfall(): ShortfallRow[] {
    return toShortfall(this.errors);
  }

  /** CSV row errors (`CSV_HAS_ERRORS`). */
  get rowErrors(): CsvImportError[] {
    return this.errors.filter(
      (e): e is CsvImportError =>
        !!e &&
        typeof e === 'object' &&
        typeof (e as CsvImportError).row === 'number'
    );
  }
}

export const toShortfall = (
  errors: unknown[] | undefined | null
): ShortfallRow[] =>
  (errors ?? [])
    .filter(
      (e): e is Partial<ShortfallRow> & { sku: string } =>
        !!e &&
        typeof e === 'object' &&
        typeof (e as ShortfallRow).sku === 'string'
    )
    .map((e) => {
      const requested = Number(e.requested ?? 0);
      const available = Number(e.available ?? 0);
      return {
        sku: e.sku,
        requested,
        available,
        shortfall:
          e.shortfall != null
            ? Number(e.shortfall)
            : Math.max(0, requested - available),
      };
    });

const isRecord = (v: unknown): v is Record<string, unknown> =>
  !!v && typeof v === 'object' && !Array.isArray(v);

/**
 * Normalise anything thrown by axios (or our own code) into a
 * WarehouseApiError. Understands both envelope dialects the backend emits:
 *
 *  - WarehouseExceptionFilter: `{ status:'Failed', error:<human>, code:<CODE>, errors? }`
 *  - TRD example / raw Nest:    `{ status:'Failed', error:<CODE>, message:<human>, errors? }`
 *  - class-validator:           `{ message: string[] }`
 */
export const parseWarehouseError = (err: unknown): WarehouseApiError => {
  if (err instanceof WarehouseApiError) return err;
  if (isAbortError(err)) return abortError();

  const anyErr = err as {
    response?: { status?: number; data?: unknown };
    message?: string;
    request?: unknown;
  };
  const response = anyErr?.response;

  if (!response) {
    if (anyErr?.request) {
      return new WarehouseApiError(
        'Tidak bisa terhubung ke server. Periksa koneksi lalu coba lagi.',
        { code: 'NETWORK' }
      );
    }
    return new WarehouseApiError(
      err instanceof Error ? err.message : 'Terjadi kesalahan tak terduga'
    );
  }

  const status = response.status ?? null;
  const body = response.data;
  let message: string | null = null;
  let code: string | null = null;
  let errors: unknown[] = [];

  if (isRecord(body)) {
    const error = typeof body.error === 'string' ? body.error : null;
    const msg = body.message;
    if (typeof body.code === 'string') code = body.code;
    if (error && MACHINE_CODE.test(error) && !code) {
      code = error;
      message = typeof msg === 'string' ? msg : null;
    } else if (error) {
      message = error;
    }
    if (!message) {
      if (typeof msg === 'string') message = msg;
      else if (Array.isArray(msg)) {
        message = msg.map(String).join('; ');
        code = code ?? 'VALIDATION';
      }
    }
    if (Array.isArray(body.errors)) errors = body.errors;
  } else if (typeof body === 'string' && body.trim()) {
    message = body.trim();
  }

  if (!message) {
    message =
      status === 404
        ? 'Data tidak ditemukan'
        : status && status >= 500
          ? 'Server sedang bermasalah. Coba lagi sebentar lagi.'
          : anyErr?.message || 'Permintaan gagal';
  }

  return new WarehouseApiError(message, { status, code, errors });
};

/** Human message for a toast, whatever was thrown. */
export const errorMessage = (err: unknown): string =>
  parseWarehouseError(err).message;

/* ------------------------------ bulk items ------------------------------ */

/**
 * Bulk item code of a transient failure (lock / deadlock): nothing changed for
 * that item and it is safe to send it again. INTERNAL (unexpected, nothing
 * changed) and domain codes are final for this attempt.
 */
const BULK_RETRYABLE = 'RETRYABLE';

/** Split failed bulk items into the ones worth retrying and the rest. */
export const splitRetryable = <R extends { error: { error: string } | null }>(
  failed: R[]
): { retryable: R[]; others: R[] } => ({
  retryable: failed.filter((r) => r.error?.error === BULK_RETRYABLE),
  others: failed.filter((r) => r.error?.error !== BULK_RETRYABLE),
});

/* ----------------------------- cancellation ----------------------------- */

/** Machine code of a request the caller cancelled (never shown to the user). */
export const ABORTED = 'ABORTED';

const abortError = () =>
  new WarehouseApiError('Permintaan dibatalkan', { code: ABORTED });

/**
 * True for a request cancelled through its AbortSignal — axios
 * `CanceledError`, a DOM `AbortError` or our own normalised error. Callers
 * drop these silently: the screen that asked has moved on.
 */
export const isAbortError = (err: unknown): boolean => {
  if (err instanceof WarehouseApiError) return err.code === ABORTED;
  const e = err as { name?: string; code?: string } | null;
  return (
    !!e &&
    (e.name === 'CanceledError' ||
      e.name === 'AbortError' ||
      e.code === 'ERR_CANCELED')
  );
};

/** Per-call transport options. `signal` aborts the request. */
export interface RequestOptions {
  signal?: AbortSignal;
}

/* ----------------------------- plumbing ------------------------------ */

type QueryValue = string | number | boolean | null | undefined;

/** Drop empty params so the backend never sees `?status=` or `hubID=null`. */
export const cleanParams = (
  params: Record<string, QueryValue>
): Record<string, string | number | boolean> => {
  const out: Record<string, string | number | boolean> = {};
  Object.entries(params).forEach(([k, v]) => {
    if (v === undefined || v === null || v === '') return;
    out[k] = v;
  });
  return out;
};

const unwrap = <T>(res: AxiosResponse): T => {
  const body = res.data as unknown;
  if (
    isRecord(body) &&
    'data' in body &&
    ('status' in body || 'message' in body)
  ) {
    return body.data as T;
  }
  return body as T;
};

const call = async <T>(request: () => Promise<AxiosResponse>): Promise<T> => {
  try {
    return unwrap<T>(await request());
  } catch (err) {
    throw parseWarehouseError(err);
  }
};

/**
 * In-flight GETs keyed by path + params. A second caller asking for the same
 * thing while it is on the wire (two widgets reading one summary, React
 * StrictMode's mount → unmount → mount) joins the first request instead of
 * firing another. Each caller keeps its own AbortSignal: leaving only drops
 * that caller; the request itself is aborted once nobody is waiting for it.
 * The release is deferred a microtask so StrictMode's immediate re-subscribe
 * keeps the request alive.
 */
interface Inflight {
  promise: Promise<unknown>;
  controller: AbortController;
  waiting: number;
}

const inflight = new Map<string, Inflight>();

/** A mutation may change what any in-flight GET returns — stop sharing them. */
const invalidateInflight = () => inflight.clear();
// …and so does the next user: sign-out drops them too.
onSessionClear(invalidateInflight);

const sortedParams = (params: Record<string, string | number | boolean>) =>
  JSON.stringify(
    Object.keys(params)
      .sort()
      .map((k) => [k, params[k]])
  );

const shared = <T>(
  key: string,
  start: (signal: AbortSignal) => Promise<T>,
  signal?: AbortSignal
): Promise<T> => {
  if (signal?.aborted) return Promise.reject(abortError());
  let entry = inflight.get(key);
  if (!entry) {
    const controller = new AbortController();
    const created: Inflight = {
      controller,
      waiting: 0,
      promise: start(controller.signal).finally(() => {
        if (inflight.get(key) === created) inflight.delete(key);
      }),
    };
    // Each joiner handles the outcome; the shared promise itself must not
    // surface as an unhandled rejection.
    created.promise.catch(() => undefined);
    inflight.set(key, created);
    entry = created;
  }
  const e = entry;
  e.waiting += 1;

  return new Promise<T>((resolve, reject) => {
    let settled = false;
    const leave = () => {
      e.waiting -= 1;
      signal?.removeEventListener('abort', onAbort);
    };
    const onAbort = () => {
      if (settled) return;
      settled = true;
      leave();
      reject(abortError());
      if (e.waiting === 0) {
        void Promise.resolve().then(() => {
          if (e.waiting > 0) return;
          if (inflight.get(key) === e) inflight.delete(key);
          e.controller.abort();
        });
      }
    };
    signal?.addEventListener('abort', onAbort);
    e.promise.then(
      (value) => {
        if (settled) return;
        settled = true;
        leave();
        resolve(value as T);
      },
      (err) => {
        if (settled) return;
        settled = true;
        leave();
        reject(err);
      }
    );
  });
};

/** Lists are `{data, pagination}` (every list endpoint, whenever `page` is sent). */
const asPage = <T>(
  raw: Paginated<T> | null | undefined,
  limit = 20
): Paginated<T> => {
  const data = raw?.data ?? [];
  const p = raw?.pagination;
  return {
    data,
    pagination: {
      page: Number(p?.page ?? 1),
      size: Number(p?.size ?? limit),
      lastPage: Number(p?.lastPage ?? 1),
      total: Number(p?.total ?? data.length),
    },
  };
};

const get = <T>(
  path: string,
  params: Record<string, QueryValue> = {},
  opts: RequestOptions = {}
): Promise<T> => {
  const cleaned = cleanParams(params);
  return shared<T>(
    `${path}?${sortedParams(cleaned)}`,
    (signal) =>
      call<T>(() => Axios.get(`${BASE}${path}`, { params: cleaned, signal })),
    opts.signal
  );
};

/** GET outside `/v1/warehouses` (the address module) — same envelope / errors. */
const getAt = <T>(
  url: string,
  params: Record<string, QueryValue> = {},
  opts: RequestOptions = {}
): Promise<T> => {
  const cleaned = cleanParams(params);
  return shared<T>(
    `${url}?${sortedParams(cleaned)}`,
    (signal) => call<T>(() => Axios.get(url, { params: cleaned, signal })),
    opts.signal
  );
};

const getPage = async <T>(
  path: string,
  params: Record<string, QueryValue>,
  opts?: RequestOptions
): Promise<Paginated<T>> =>
  asPage<T>(
    await get<Paginated<T>>(path, params, opts),
    Number(params.limit ?? 20)
  );

const post = <T>(path: string, body?: unknown) => {
  invalidateInflight();
  return call<T>(() => Axios.post(`${BASE}${path}`, body ?? {}));
};

const patch = <T>(path: string, body?: unknown) => {
  invalidateInflight();
  return call<T>(() => Axios.patch(`${BASE}${path}`, body ?? {}));
};

const put = <T>(path: string, body?: unknown) => {
  invalidateInflight();
  return call<T>(() => Axios.put(`${BASE}${path}`, body ?? {}));
};

/** Occupancy filters → query params (`status` as a comma list). */
const occupancyParams = (
  q: StorageOccupancyFilterQuery & { page?: number; limit?: number }
): Record<string, QueryValue> => ({
  hubID: q.hubID,
  locationID: q.locationID ?? undefined,
  sectionID: q.sectionID ?? undefined,
  clientID: q.clientID ?? undefined,
  search: q.search ?? undefined,
  status: q.status?.length ? q.status.join(',') : undefined,
  page: q.page,
  limit: q.limit,
});

/* ------------------------------ CSV I/O ------------------------------ */

export interface CsvImportContext extends HubClientSnapshot {}

const csvForm = (ctx: CsvImportContext, file: File): FormData => {
  const form = new FormData();
  form.append('file', file);
  form.append('hubID', String(ctx.hubID));
  form.append('hubName', ctx.hubName);
  if (ctx.hubCode) form.append('hubCode', ctx.hubCode);
  form.append('clientID', String(ctx.clientID));
  form.append('clientName', ctx.clientName);
  if (ctx.clientCode) form.append('clientCode', ctx.clientCode);
  return form;
};

const postMultipart = <T>(
  path: string,
  form: FormData,
  opts: RequestOptions = {}
) => {
  invalidateInflight();
  return call<T>(() =>
    Axios.post(`${BASE}${path}`, form, {
      headers: { 'Content-Type': 'multipart/form-data' },
      ...(opts.signal ? { signal: opts.signal } : {}),
    })
  );
};

/** `responseType: 'blob'` hides JSON error bodies inside a Blob — read it back. */
const parseBlobError = async (err: unknown): Promise<WarehouseApiError> => {
  const data = (err as { response?: { data?: unknown } })?.response?.data;
  if (data instanceof Blob) {
    try {
      const text = await data.text();
      const json = JSON.parse(text);
      (err as { response: { data: unknown } }).response.data = json;
    } catch {
      /* not JSON — fall through to the generic parse */
    }
  }
  return parseWarehouseError(err);
};

const filenameFrom = (disposition: unknown, fallback: string): string => {
  if (typeof disposition !== 'string') return fallback;
  const m = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(disposition);
  if (!m) return fallback;
  try {
    return decodeURIComponent(m[1]);
  } catch {
    return m[1];
  }
};

/** `{prefix}-{DDMMYYYY}.csv` (WIB) — the name the server gives a stock CSV. */
const datedCsvName = (prefix: string): string => {
  const [y, m, d] = todayJakarta().split('-');
  return `${prefix}-${d}${m}${y}.csv`;
};

/**
 * Authenticated GET of a file (the shared axios adds the bearer token): the
 * bytes and the server's `Content-Disposition` name, `fallbackName` when the
 * header is missing. A failure comes back as a {@link WarehouseApiError} like
 * every other call; `opts.signal` cancels the download.
 */
const getFile = async (
  path: string,
  params: Record<string, QueryValue>,
  fallbackName: string,
  opts: RequestOptions = {}
): Promise<CsvFile> => {
  try {
    const res = await Axios.get(`${BASE}${path}`, {
      params: cleanParams(params),
      responseType: 'blob',
      ...(opts.signal ? { signal: opts.signal } : {}),
    });
    return {
      blob: res.data as Blob,
      filename: filenameFrom(
        res.headers?.['content-disposition'],
        fallbackName
      ),
    };
  } catch (err) {
    throw await parseBlobError(err);
  }
};

/* ------------------------------- client ------------------------------ */

/** The backend's page size cap (`limit` max). */
const MAX_PAGE_LIMIT = 200;

/** Query of a period CSV: the SKU codes go out comma-joined as `skus`. */
const periodParams = ({
  skus,
  ...rest
}: StockPeriodCsvQuery): Record<string, QueryValue> => ({
  ...rest,
  skus: skus?.length ? skus.join(',') : null,
});

/** Server-searched, paginated SKU catalog (page is always sent). */
const listSkus = (
  q: ListSkusQuery = {},
  opts?: RequestOptions
): Promise<Paginated<WarehouseSku>> =>
  getPage<WarehouseSku>(
    '/skus',
    {
      clientID: q.clientID,
      search: q.search?.trim() || null,
      skus: q.skus?.length ? q.skus.join(',') : null,
      status: q.status,
      productCategory: q.productCategory?.trim() || null,
      page: q.page ?? 1,
      limit: q.limit ?? 20,
    },
    opts
  );

/** Server-paged storages of a hub (page is always sent), in tree order. */
const listStoragesPage = (
  q: ListStoragesPageQuery,
  opts?: RequestOptions
): Promise<Paginated<WarehouseStorage>> =>
  getPage<WarehouseStorage>(
    '/storages',
    {
      hubID: q.hubID,
      category: q.category,
      type: q.type,
      status: q.status,
      search: q.search?.trim() || null,
      parentID: q.parentID,
      withStock: q.withStock,
      page: q.page ?? 1,
      limit: q.limit ?? 20,
    },
    opts
  );

const sourcesPath = (id: string) =>
  `/goods-issues/${encodeURIComponent(id)}/sources`;

/**
 * Stock rows (location + batch) the goods issue can reserve `sku` from, FEFO
 * order; `available` includes what this goods issue already holds. `page`
 * sent → `{data, pagination}` (the picker's list, `search` = batch number or
 * name path); without it → the plain array (all of them, e.g. one storage's).
 */
function listGoodsIssueSources(
  id: string,
  q: GoodsIssueSourcesQuery & { page: number },
  opts?: RequestOptions
): Promise<Paginated<GoodsIssueSource>>;
function listGoodsIssueSources(
  id: string,
  q: GoodsIssueSourcesQuery,
  opts?: RequestOptions
): Promise<GoodsIssueSource[]>;
function listGoodsIssueSources(
  id: string,
  q: GoodsIssueSourcesQuery,
  opts?: RequestOptions
): Promise<Paginated<GoodsIssueSource> | GoodsIssueSource[]> {
  const params = {
    sku: q.sku,
    storageID: q.storageID,
    search: q.search?.trim() || null,
    page: q.page,
    limit: q.limit,
  };
  return q.page === undefined
    ? get<GoodsIssueSource[]>(sourcesPath(id), params, opts).then(
        (r) => r ?? []
      )
    : getPage<GoodsIssueSource>(sourcesPath(id), params, opts);
}

/**
 * The distinct storages holding candidates for `sku` (earliest expiry first):
 * the Item location picker's list. `page` sent → paged; without it → the plain
 * array (the server caps it).
 */
function listGoodsIssueSourceStorages(
  id: string,
  q: GoodsIssueSourceStoragesQuery & { page: number },
  opts?: RequestOptions
): Promise<Paginated<GoodsIssueSourceStorage>>;
function listGoodsIssueSourceStorages(
  id: string,
  q: GoodsIssueSourceStoragesQuery,
  opts?: RequestOptions
): Promise<GoodsIssueSourceStorage[]>;
function listGoodsIssueSourceStorages(
  id: string,
  q: GoodsIssueSourceStoragesQuery,
  opts?: RequestOptions
): Promise<Paginated<GoodsIssueSourceStorage> | GoodsIssueSourceStorage[]> {
  const path = `${sourcesPath(id)}/storages`;
  const params = {
    sku: q.sku,
    search: q.search?.trim() || null,
    page: q.page,
    limit: q.limit,
  };
  return q.page === undefined
    ? get<GoodsIssueSourceStorage[]>(path, params, opts).then((r) => r ?? [])
    : getPage<GoodsIssueSourceStorage>(path, params, opts);
}

/** `ids=` takes at most this many storages per request. */
const MAX_STORAGE_IDS = 100;

export const warehouseApi = {
  /* stock & ledger (v1 + summary) */
  /** Paginated (page is always sent); zero-qty batches never come back. */
  listStocks: (q: ListStocksQuery, opts?: RequestOptions) =>
    getPage<WarehouseStock>(
      '/stocks',
      { ...q, page: q.page ?? 1, limit: q.limit ?? 20 },
      opts
    ),
  /** Tiles: same filters as the stock list. */
  getStockSummary: (q: StockSummaryQuery, opts?: RequestOptions) =>
    get<StockSummary>('/stocks/summary', { ...q }, opts),
  /**
   * Allocatable qty per SKU in a hub — the rule the backend reserves by
   * (active bins under a STORAGE location). Unknown SKUs come back as 0.
   */
  getStockAvailability: (
    q: { hubID: number; clientID: number; skus: string[] },
    opts?: RequestOptions
  ): Promise<StockAvailability[]> =>
    q.skus.length
      ? get<StockAvailability[]>(
          '/stocks/availability',
          { hubID: q.hubID, clientID: q.clientID, skus: q.skus.join(',') },
          opts
        ).then((r) => r ?? [])
      : Promise.resolve([]),
  /**
   * Inventory CSV: the stock list's filters (zero on-hand rows stay out), one
   * row per batch or summed per `groupBy`.
   */
  downloadStockInventoryCsv: (
    q: StockInventoryCsvQuery,
    opts?: RequestOptions
  ): Promise<CsvFile> =>
    getFile('/stocks/export', { ...q }, datedCsvName('inventory'), opts),
  /** "Laporan Stock" CSV: one row per ledger movement in the period. */
  downloadStockMovementsCsv: (
    q: StockPeriodCsvQuery,
    opts?: RequestOptions
  ): Promise<CsvFile> =>
    getFile(
      '/stock-movements/export',
      periodParams(q),
      datedCsvName('Laporan Stock'),
      opts
    ),
  /** "Laporan Mutasi" CSV: opening, in, out and closing stock per day and SKU. */
  downloadStockMutationsCsv: (
    q: StockPeriodCsvQuery,
    opts?: RequestOptions
  ): Promise<CsvFile> =>
    getFile(
      '/stock-mutations/export',
      periodParams(q),
      datedCsvName('Laporan Mutasi'),
      opts
    ),
  /** Newest first, capped at `limit` (backend default and max 500). */
  listMovements: (
    q: {
      hubID: number;
      type?: StockMovementType;
      clientID?: number | null;
      /** Any level — the storage's whole subtree. */
      storageID?: string | null;
      sku?: string;
      refCode?: string;
      dateFrom?: string;
      dateTo?: string;
      limit?: number;
    },
    opts?: RequestOptions
  ) => get<StockMovement[]>('/movements', q, opts).then((r) => r ?? []),

  /* storages */
  /** `withStock` adds each storage's own `qtyOnHand`. */
  listStorages: (
    q: {
      hubID: number;
      category?: StorageCategory;
      type?: StorageType;
      status?: StorageStatus;
      withStock?: boolean;
      /** Code or name (ILIKE). */
      search?: string | null;
    },
    opts?: RequestOptions
  ) => get<WarehouseStorage[]>('/storages', q, opts).then((r) => r ?? []),
  /** Server-searched, paginated storages — the picker's list. */
  listStoragesPage,
  /** Resolve exact storages (labels of values already chosen), any status. */
  getStoragesByIds: async (
    hubID: number,
    ids: readonly string[],
    opts?: RequestOptions
  ): Promise<WarehouseStorage[]> => {
    const unique = Array.from(new Set(ids.filter(Boolean))).sort();
    const chunks: string[][] = [];
    for (let i = 0; i < unique.length; i += MAX_STORAGE_IDS)
      chunks.push(unique.slice(i, i + MAX_STORAGE_IDS));
    const rows = await Promise.all(
      chunks.map((chunk) =>
        get<WarehouseStorage[]>(
          '/storages',
          { hubID, ids: chunk.join(',') },
          opts
        ).then((r) => r ?? [])
      )
    );
    return rows.reduce<WarehouseStorage[]>((all, r) => all.concat(r), []);
  },
  createStorage: (body: CreateStoragePayload) =>
    post<WarehouseStorage>('/storages', body),
  updateStorage: (id: string, body: UpdateStoragePayload) =>
    patch<WarehouseStorage>(`/storages/${encodeURIComponent(id)}`, body),

  /* storage capacity (docs/warehouse/storage-occupancy-layout.md in the BE repo) */
  /** CSV `path|name, capacity_cbm, capacity_kg, capacity_qty, width_cm, depth_cm, height_cm`; atomic per file. */
  importStorageCapacity: (hubID: number, file: File, opts?: RequestOptions) => {
    const form = new FormData();
    form.append('file', file);
    form.append('hubID', String(hubID));
    return postMultipart<CapacityImportResult>(
      '/storages/capacity/import',
      form,
      opts
    );
  },
  /** Template with current values (one row per section and bin). */
  downloadStorageCapacityCsv: (
    hubID: number,
    opts?: RequestOptions
  ): Promise<CsvFile> =>
    getFile(
      '/storages/capacity/export',
      { hubID },
      datedCsvName('storage-capacity'),
      opts
    ),

  /* storage occupancy */
  /** One row per BIN with its stock lines, plus the summary tiles. */
  listStorageOccupancy: (q: ListStorageOccupancyQuery, opts?: RequestOptions) =>
    get<StorageOccupancyPage>(
      '/storages/occupancy',
      occupancyParams({ ...q, page: q.page ?? 1, limit: q.limit ?? 20 }),
      opts
    ).then((r) => ({
      ...asPage<StorageOccupancyRow>(r, q.limit ?? 20),
      summary: r?.summary ?? {
        binActive: 0,
        binUsed: 0,
        binUnset: 0,
        binHot: 0,
        usedCbm: 0,
        capacityCbm: 0,
        pctCbm: null,
        statusCounts: {},
      },
    })),
  /**
   * One card per location (paginated, ≤ 8 sections per card — `sectionCount`
   * carries the rest).
   */
  getStorageOccupancyBoard: (
    q: StorageOccupancyBoardQuery,
    opts?: RequestOptions
  ): Promise<Paginated<StorageOccupancyBoardLocation>> =>
    getPage<StorageOccupancyBoardLocation>(
      '/storages/occupancy/board',
      {
        hubID: q.hubID,
        clientID: q.clientID ?? null,
        search: q.search ?? null,
        sort: q.sort ?? null,
        page: q.page ?? 1,
        limit: q.limit ?? 12,
      },
      opts
    ),
  /**
   * One paged level of the occupancy drill-down: the hub's locations, a
   * location's sections or a section's bins (never the whole tree).
   */
  listStorageOccupancyChildren: (
    q: StorageOccupancyChildrenQuery,
    opts?: RequestOptions
  ): Promise<StorageOccupancyChildren> =>
    get<StorageOccupancyChildren>(
      '/storages/occupancy/children',
      {
        hubID: q.hubID,
        parentID: q.parentID ?? null,
        clientID: q.clientID ?? null,
        search: q.search ?? null,
        sort: q.sort ?? null,
        basis: q.basis ?? null,
        page: q.page ?? 1,
        limit: q.limit ?? 12,
      },
      opts
    ).then((r) => ({
      ...asPage<StorageOccupancyChild>(r, q.limit ?? 12),
      parent: r?.parent
        ? { ...r.parent, ancestors: r.parent.ancestors ?? [] }
        : null,
    })),
  /**
   * "Utilisasi lokasi" in one call (BE doc § 7.2): the hub and one page of its
   * locations on a basis (m³ / kg / unit), thresholds, locations to act on
   * and 30-day trends.
   */
  getLocationUtilization: (
    q: LocationUtilizationQuery,
    opts?: RequestOptions
  ): Promise<LocationUtilizationPayload> =>
    get<LocationUtilizationPayload>(
      '/storages/occupancy/locations',
      {
        hubID: q.hubID,
        clientID: q.clientID ?? null,
        basis: q.basis,
        search: q.search || null,
        minPct: q.minPct ?? null,
        maxPct: q.maxPct ?? null,
        page: q.page ?? 1,
        limit: q.limit ?? 12,
        attentionPage: q.attentionPage ?? 1,
        attentionLimit: q.attentionLimit ?? 5,
      },
      opts
    ).then((r) => ({
      ...r,
      basis: r?.basis ?? q.basis,
      locations: r?.locations ?? [],
      // Page 1, 12 a page, every location on it: an answer without `pagination`.
      pagination: asPage<LocationUtilization>(
        {
          data: r?.locations ?? [],
          pagination: r?.pagination,
        } as Paginated<LocationUtilization>,
        q.limit ?? 12
      ).pagination,
      attention: r?.attention ?? [],
      // Page 1, 5 a page, every attention row on it: an answer without it.
      attentionPagination: asPage<LocationUtilizationAttention>(
        {
          data: r?.attention ?? [],
          pagination: r?.attentionPagination,
        } as Paginated<LocationUtilizationAttention>,
        q.attentionLimit ?? 5
      ).pagination,
      excluded: r?.excluded ?? [],
    })),
  downloadStorageOccupancyCsv: (
    q: StorageOccupancyFilterQuery,
    opts?: RequestOptions
  ): Promise<CsvFile> =>
    getFile(
      '/storages/occupancy/export',
      occupancyParams(q),
      datedCsvName('storage-occupancy'),
      opts
    ),

  /* warehouse map & layout */
  /**
   * Columnar occupancy + layout of a hub. Sends `If-None-Match`; a 304 comes
   * back as `{ payload: null }` so the caller keeps its arrays.
   */
  getStorageMap: async (
    q: { hubID: number; clientID?: number | null },
    etag: string | null,
    opts?: RequestOptions
  ): Promise<WarehouseMapResult> => {
    const res = await call<WarehouseMapPayload | null>(() =>
      Axios.get(`${BASE}/storages/map`, {
        params: cleanParams({ hubID: q.hubID, clientID: q.clientID ?? null }),
        headers: etag ? { 'If-None-Match': etag } : {},
        validateStatus: (s) => (s >= 200 && s < 300) || s === 304,
        ...(opts?.signal ? { signal: opts.signal } : {}),
      })
    );
    // 304 has an empty body ('' / undefined): keep the caller's arrays.
    return !res || typeof res !== 'object'
      ? { payload: null, etag }
      : { payload: res, etag: res.etag ?? etag };
  },
  getStorageLayout: (hubID: number, opts?: RequestOptions) =>
    get<StorageLayout>('/storages/layout', { hubID }, opts).then(
      (r) => r ?? { floor: null, layouts: [] }
    ),
  /** Replaces the hub's whole layout; 409 LAYOUT_STALE when `version` is old. */
  saveStorageLayout: (body: SaveStorageLayoutPayload) =>
    put<SaveStorageLayoutResult>('/storages/layout', body),

  /* SKU catalog */
  listSkus,
  /** Resolve exact SKU codes of one client (lines already chosen). */
  getSkusByCode: (
    q: { clientID: number; skus: string[] },
    opts?: RequestOptions
  ): Promise<WarehouseSku[]> =>
    q.skus.length
      ? listSkus(
          {
            clientID: q.clientID,
            skus: q.skus,
            page: 1,
            limit: Math.min(MAX_PAGE_LIMIT, q.skus.length),
          },
          opts
        ).then((r) => r.data)
      : Promise.resolve([]),
  createSku: (body: CreateSkuPayload) => post<WarehouseSku>('/skus', body),
  updateSku: (id: string, body: UpdateSkuPayload) =>
    patch<WarehouseSku>(`/skus/${encodeURIComponent(id)}`, body),
  importSkus: (body: {
    clientID: number;
    clientName: string;
    rows: SkuRowPayload[];
  }) => post<ImportSkusResult>('/skus/import', body),

  /* vendor master (global) */
  listVendors: (q: ListVendorsQuery = {}, opts?: RequestOptions) =>
    getPage<Vendor>('/vendors', { ...q }, opts),
  getVendor: (id: string, opts?: RequestOptions) =>
    get<Vendor>(`/vendors/${encodeURIComponent(id)}`, {}, opts),
  createVendor: (body: CreateVendorPayload) => post<Vendor>('/vendors', body),
  updateVendor: (id: string, body: UpdateVendorPayload) =>
    patch<Vendor>(`/vendors/${encodeURIComponent(id)}`, body),
  importVendors: (rows: VendorRowPayload[]) =>
    post<ImportVendorsResult>('/vendors/import', { rows }),

  /* outbound request — input */
  createOutboundOrder: async (
    body: CreateOutboundOrderPayload
  ): Promise<CreateOutboundOrderResult> => {
    const raw = await post<
      | CreateOutboundOrderResult
      | (OutboundOrder & {
          result?: 'CREATED' | 'EXISTS';
          stockWarnings?: CreateOutboundOrderResult['stockWarnings'];
        })
    >('/outbound-orders', body);
    // Entity shape is {result, order, stockWarnings}; the TRD example flattens
    // the order into the root. Accept both.
    if (raw && 'order' in raw && raw.order)
      return raw as CreateOutboundOrderResult;
    const flat = raw as OutboundOrder & {
      result?: 'CREATED' | 'EXISTS';
      stockWarnings?: CreateOutboundOrderResult['stockWarnings'];
    };
    return {
      result: flat.result ?? 'CREATED',
      order: flat,
      stockWarnings: flat.stockWarnings ?? [],
    };
  },
  /** Is this client ref already an order of the client? (asked while typing) */
  checkOutboundExternalId: (
    clientID: number,
    externalID: string,
    opts?: RequestOptions
  ) =>
    get<ExternalIdCheck>(
      '/outbound-orders/external-id',
      { clientID, externalID },
      opts
    ),
  downloadImportTemplate: (clientID: number): Promise<CsvFile> =>
    getFile(
      '/outbound-orders/import/template',
      { clientID },
      'template-outbound-request.csv'
    ),
  /** PDF straight to the main bucket; send `fileUrl` with the document it belongs to. */
  uploadDocument: (file: File, opts?: RequestOptions) => {
    const form = new FormData();
    form.append('file', file);
    return postMultipart<UploadedDocument>('/uploads/main', form, opts);
  },
  /** SKU sample image (JPG / PNG / WebP, 5 MB) — same bucket; send `fileUrl` as `imageUrl`. */
  uploadImage: (file: File, opts?: RequestOptions) => {
    const form = new FormData();
    form.append('file', file);
    return postMultipart<UploadedDocument>('/uploads/images', form, opts);
  },
  dryRunImport: (ctx: CsvImportContext, file: File) =>
    postMultipart<CsvDryRunResult>(
      '/outbound-orders/import:dry-run',
      csvForm(ctx, file)
    ),
  commitImport: (ctx: CsvImportContext, file: File) =>
    postMultipart<CsvCommitResult>(
      '/outbound-orders/import:commit',
      csvForm(ctx, file)
    ),
  listImports: (
    q: {
      hubID?: number | null;
      clientID?: number | null;
      page?: number;
      limit?: number;
    },
    opts?: RequestOptions
  ) => getPage<OutboundOrderUpload>('/outbound-orders/imports', q, opts),

  /* outbound request — list & actions */
  listOutboundOrders: (q: ListOutboundOrdersQuery, opts?: RequestOptions) =>
    getPage<OutboundOrder>('/outbound-orders', { ...q }, opts),
  /** The city the server would store for this address text (its master
   *  match; no geocoding) — or a warning. */
  resolveOutboundCity: (q: { address: string }, opts?: RequestOptions) =>
    get<ResolveCityResult>('/outbound-orders/resolve-city', { ...q }, opts),
  /** Core-service's city master (the "Kota" select); ~500 rows, cached. */
  listCities: (opts?: RequestOptions) =>
    get<{ data: CityOption[] }>('/cities', {}, opts).then((r) => r.data),
  getOutboundSummary: (q: OutboundOrderSummaryQuery, opts?: RequestOptions) =>
    get<OutboundOrderSummary>('/outbound-orders/summary', { ...q }, opts),
  /** CSV of the list as filtered + tabbed (every page) — one row per order line. */
  downloadOutboundOrdersCsv: (
    q: ExportOutboundOrdersQuery,
    opts?: RequestOptions
  ): Promise<CsvFile> =>
    getFile(
      '/outbound-orders/export',
      { ...q },
      datedCsvName('outbound-request'),
      opts
    ),
  getOutboundOrder: (id: string, opts?: RequestOptions) =>
    get<OutboundOrderDetail>(
      `/outbound-orders/${encodeURIComponent(id)}`,
      {},
      opts
    ),
  /** Recipient + destination until In delivery; a Dash shipment still at
   *  the hub is rewritten with it (409 SHIPMENT_IN_TRANSIT once a rider has it). */
  updateOutboundOrder: (id: string, body: UpdateOutboundOrderPayload) =>
    patch<OutboundOrder>(`/outbound-orders/${encodeURIComponent(id)}`, body),
  cancelOutboundOrder: (id: string, reason: string) =>
    post<CancelOutboundOrderResult>(
      `/outbound-orders/${encodeURIComponent(id)}/cancel`,
      { reason }
    ),
  createPickingOrder: (id: string) =>
    post<{ detail: GoodsIssueDetail }>(
      `/outbound-orders/${encodeURIComponent(id)}/picking-order`
    ),
  bulkCreatePickingOrders: (outboundOrderIDs: string[]) =>
    post<{ results: BulkPickingResult[] }>(
      '/outbound-orders/picking-orders/bulk',
      { outboundOrderIDs }
    ),
  /** Label data of orders shipped with a 3PL — they have no Dash shipment. */
  getOutboundShippingLabels: (outboundOrderIDs: string[]) =>
    post<{ labels: OutboundOrderShippingLabel[] }>(
      '/outbound-orders/shipping-labels/bulk',
      { outboundOrderIDs }
    ),

  /* goods issue — picking & shipping */
  listGoodsIssues: (q: ListGoodsIssuesQuery, opts?: RequestOptions) =>
    getPage<GoodsIssueSummary>('/goods-issues', { ...q }, opts),
  /** Tab counts of a stage, for the list's filters + search (not its status). */
  getGoodsIssueSummary: (q: GoodsIssueSummaryQuery, opts?: RequestOptions) =>
    get<GoodsIssueStageSummary>('/goods-issues/summary', { ...q }, opts),
  getGoodsIssue: (id: string, opts?: RequestOptions) =>
    get<GoodsIssueDetail>(`/goods-issues/${encodeURIComponent(id)}`, {}, opts),
  setPicker: (id: string, pickerName: string, expectedUpdatedAt?: string) =>
    patch<{ detail: GoodsIssueDetail }>(
      `/goods-issues/${encodeURIComponent(id)}/picker`,
      expectedUpdatedAt ? { pickerName, expectedUpdatedAt } : { pickerName }
    ),
  listGoodsIssueSources,
  listGoodsIssueSourceStorages,
  /** Replace the whole reservation list (edit picked items). */
  updateGoodsIssueLines: (id: string, body: UpdateGoodsIssueLinesPayload) =>
    put<{ detail: GoodsIssueDetail }>(
      `/goods-issues/${encodeURIComponent(id)}/lines`,
      body
    ),
  /**
   * RESERVED / PICKED → READY_TO_PICKUP. Sets the picker and readies in one
   * transaction; a 422 VALIDATION names `pickerName`. `expectedUpdatedAt`
   * (the loaded version) makes a changed row answer 409 GOODS_ISSUE_STALE.
   */
  readyToPickup: (
    id: string,
    pickerName: string,
    expectedUpdatedAt?: string,
    /** The outbound order's destination city, when it had none yet. */
    destinationCityID?: number
  ) =>
    post<{ detail: GoodsIssueDetail }>(
      `/goods-issues/${encodeURIComponent(id)}/ready-to-pickup`,
      {
        pickerName,
        ...(expectedUpdatedAt ? { expectedUpdatedAt } : {}),
        ...(destinationCityID ? { destinationCityID } : {}),
      }
    ),
  cancelGoodsIssue: (id: string) =>
    post<unknown>(`/goods-issues/${encodeURIComponent(id)}/cancel`),
  /** "Batal siap diambil": READY_TO_PICKUP → PICKED, back to picking. */
  undoReadyToPickup: (id: string, reason: string, expectedUpdatedAt?: string) =>
    post<{ detail: GoodsIssueDetail }>(
      `/goods-issues/${encodeURIComponent(id)}/undo-ready`,
      expectedUpdatedAt ? { reason, expectedUpdatedAt } : { reason }
    ),
  /**
   * "Batal submit shipping": SHIPPED → READY_TO_PICKUP while the goods are
   * still at the hub (not dispatched). Carrier / AWB / shipment cleared, stock
   * back on hand and reserved again.
   */
  undoShipping: (id: string, reason: string, expectedUpdatedAt?: string) =>
    post<{ detail: GoodsIssueDetail }>(
      `/goods-issues/${encodeURIComponent(id)}/undo-ship`,
      expectedUpdatedAt ? { reason, expectedUpdatedAt } : { reason }
    ),
  shipGoodsIssue: (id: string, body: SubmitShippingPayload = {}) =>
    post<SubmitShippingResult>(
      `/goods-issues/${encodeURIComponent(id)}/ship`,
      body
    ),
  bulkShip: (
    goodsIssueIDs: string[],
    carrier?: FulfillmentCarrier,
    handover?: Pick<
      SubmitShippingPayload,
      'handoverRecipientName' | 'handoverNote'
    >
  ) =>
    post<{ results: BulkShipResult[] }>('/goods-issues/ship/bulk', {
      goodsIssueIDs,
      ...(carrier ? { carrier } : {}),
      ...(carrier === 'HANDOVER' ? handover : {}),
    }),
  /** Manual carrier (Lion Parcel): In delivery / Delivered / Failed by hand. */
  updateOutboundFulfillment: (id: string, body: UpdateFulfillmentPayload) =>
    post<OutboundOrder>(
      `/outbound-orders/${encodeURIComponent(id)}/fulfillment-status`,
      body
    ),
  /** The same manual status + note for many orders; per-item results. */
  bulkUpdateOutboundFulfillment: (
    outboundOrderIDs: string[],
    body: UpdateFulfillmentPayload
  ) =>
    post<{ results: BulkFulfillmentResult[] }>(
      '/outbound-orders/fulfillment-status/bulk',
      { outboundOrderIDs, ...body }
    ),

  /* outbound destination — address module */
  getDestinationConfig: (clientId: number, opts?: RequestOptions) =>
    getAt<DestinationConfig>(
      '/v1/addresses/destination-config',
      { clientId },
      opts
    ),
  /** Cache-first geocode; 404 GEOCODE_NOT_FOUND when nothing matched. */
  geocodeAddress: (address: string, opts?: RequestOptions) =>
    getAt<GeocodeResult>('/v1/addresses/geocode', { address }, opts),
  /** The client's known places (server search). */
  listDestinationPlaces: async (
    q: { clientId: number; search?: string | null; limit?: number },
    opts?: RequestOptions
  ): Promise<DestinationPlace[]> => {
    const raw = await getAt<
      | DestinationPlace[]
      | { places?: DestinationPlace[]; data?: DestinationPlace[] }
      | null
    >(
      '/v1/addresses/places',
      { clientIds: String(q.clientId), search: q.search, limit: q.limit ?? 50 },
      opts
    );
    const places = Array.isArray(raw) ? raw : (raw?.places ?? raw?.data ?? []);
    // The lane book also lists hub origins; an outbound goes to destinations.
    return places.filter((p) => p.endpoint !== 'origin');
  },

  /* purchase order */
  /** Tab counts per PO status. */
  getPurchaseOrderSummary: (
    q: PurchaseOrderSummaryQuery,
    opts?: RequestOptions
  ) => get<PurchaseOrderSummary>('/purchase-orders/summary', { ...q }, opts),
  listPurchaseOrders: (q: ListPurchaseOrdersQuery, opts?: RequestOptions) =>
    getPage<PurchaseOrder>('/purchase-orders', { ...q }, opts),
  getPurchaseOrder: (id: string, opts?: RequestOptions) =>
    get<PurchaseOrderDetail>(
      `/purchase-orders/${encodeURIComponent(id)}`,
      {},
      opts
    ),
  /** Edit a DRAFT PO: header fields + lines replaced (409 once confirmed). */
  updateDraftPurchaseOrder: async (
    id: string,
    body: UpdateDraftPurchaseOrderPayload
  ) => {
    const raw = await put<PurchaseOrder | PurchaseOrderDetail>(
      `/purchase-orders/${encodeURIComponent(id)}`,
      body
    );
    return raw && 'order' in raw ? raw.order : (raw as PurchaseOrder);
  },
  createPurchaseOrder: async (body: CreatePurchaseOrderPayload) => {
    const raw = await post<PurchaseOrder | PurchaseOrderDetail>(
      '/purchase-orders',
      body
    );
    return raw && 'order' in raw ? raw.order : (raw as PurchaseOrder);
  },
  confirmPurchaseOrder: (id: string) =>
    post<unknown>(`/purchase-orders/${encodeURIComponent(id)}/confirm`),
  cancelPurchaseOrder: (id: string, reason: string) =>
    post<unknown>(`/purchase-orders/${encodeURIComponent(id)}/cancel`, {
      reason,
    }),

  /* inbound request & goods receipt */
  /** Tab counts per receipt status. */
  getInboundRequestSummary: (
    q: PurchaseOrderSummaryQuery,
    opts?: RequestOptions
  ) => get<InboundRequestSummary>('/inbound-requests/summary', { ...q }, opts),
  listInboundRequests: (q: ListInboundRequestsQuery, opts?: RequestOptions) =>
    getPage<PurchaseOrder>('/inbound-requests', { ...q }, opts),
  getInboundRequest: (id: string, opts?: RequestOptions) =>
    get<PurchaseOrderDetail>(
      `/inbound-requests/${encodeURIComponent(id)}`,
      {},
      opts
    ),
  createInboundReceipt: async (
    purchaseOrderID: string,
    body: CreateInboundReceiptPayload
  ) => {
    const raw = await post<GoodsReceiptDetail | GoodsReceiptSummary>(
      `/inbound-requests/${encodeURIComponent(purchaseOrderID)}/goods-receipts`,
      body
    );
    return raw && 'receipt' in raw ? raw.receipt : (raw as GoodsReceiptSummary);
  },
  listGoodsReceipts: (q: ListGoodsReceiptsQuery, opts?: RequestOptions) =>
    getPage<GoodsReceiptSummary>('/goods-receipts', { ...q }, opts),
  getGoodsReceipt: (id: string, opts?: RequestOptions) =>
    get<GoodsReceiptDetail>(
      `/goods-receipts/${encodeURIComponent(id)}`,
      {},
      opts
    ),

  /* transfer storage */
  /** Paged; `status` narrows to one tab. */
  listTransfers: (q: ListTransfersQuery, opts?: RequestOptions) =>
    getPage<TransferSummary>('/transfers', { ...q }, opts),
  /** Tab counts: the list's filters minus status / paging. */
  getTransferSummary: (q: TransferFilterQuery, opts?: RequestOptions) =>
    get<TransferStatusSummary>('/transfers/summary', { ...q }, opts),
  getTransfer: (id: string, opts?: RequestOptions) =>
    get<TransferDetail>(`/transfers/${encodeURIComponent(id)}`, {}, opts),
  /** Reserves the lines' qty at the source bin (stock moves at DONE). */
  createTransfer: (body: CreateTransferPayload) =>
    post<TransferDetail>('/transfers', body),
  approveTransfer: (id: string) =>
    post<TransferDetail>(`/transfers/${encodeURIComponent(id)}/approve`),
  prepareTransfer: (id: string) =>
    post<TransferDetail>(`/transfers/${encodeURIComponent(id)}/preparing`),
  /** Moves the stock to the destination bin. */
  completeTransfer: (id: string) =>
    post<TransferDetail>(`/transfers/${encodeURIComponent(id)}/done`),
  /** REQUESTED only; releases the reservation. */
  rejectTransfer: (id: string, reason: string) =>
    post<TransferDetail>(`/transfers/${encodeURIComponent(id)}/reject`, {
      reason,
    }),
  /** APPROVED / PREPARING only; releases the reservation too. */
  cancelTransfer: (id: string, reason: string) =>
    post<TransferDetail>(`/transfers/${encodeURIComponent(id)}/cancel`, {
      reason,
    }),

  /* transfer warehouse (hub → hub) */
  /** Paged; `status` narrows to ONE effective status (a tab). */
  listHubTransfers: (q: ListHubTransfersQuery, opts?: RequestOptions) =>
    getPage<HubTransferSummary>('/hub-transfers', { ...q }, opts),
  /** Tab counts: the list's filters minus status / paging. */
  getHubTransferSummary: (q: HubTransferFilterQuery, opts?: RequestOptions) =>
    get<HubTransferStatusSummary>('/hub-transfers/summary', { ...q }, opts),
  getHubTransfer: (id: string, opts?: RequestOptions) =>
    get<HubTransferDetail>(
      `/hub-transfers/${encodeURIComponent(id)}`,
      {},
      opts
    ),
  /** Nothing is reserved here; stock is reserved by the picking order. */
  createHubTransfer: (body: CreateHubTransferPayload) =>
    post<HubTransferDetail>('/hub-transfers', body),
  /** REQUESTED → FIRST_APPROVED (source hub); idempotent. */
  firstApproveHubTransfer: (id: string) =>
    post<HubTransferDetail>(
      `/hub-transfers/${encodeURIComponent(id)}/first-approve`
    ),
  /** FIRST_APPROVED → APPROVED (destination hub); raises the TRANSFER order. */
  approveHubTransfer: (id: string) =>
    post<HubTransferDetail>(`/hub-transfers/${encodeURIComponent(id)}/approve`),
  /** REQUESTED | FIRST_APPROVED only; the reason is required. */
  rejectHubTransfer: (id: string, reason: string) =>
    post<HubTransferDetail>(`/hub-transfers/${encodeURIComponent(id)}/reject`, {
      reason,
    }),

  /* stock opname */
  /** Paged; `status` narrows to one tab (several statuses comma-separated). */
  listStockOpnames: (q: ListStockOpnamesQuery, opts?: RequestOptions) =>
    getPage<StockOpnameSummary>('/stock-opnames', { ...q }, opts),
  /** Tab counts: the list's filters minus status / paging. */
  getStockOpnameSummary: (q: StockOpnameFilterQuery, opts?: RequestOptions) =>
    get<StockOpnameStatusSummary>('/stock-opnames/summary', { ...q }, opts),
  getStockOpname: (id: string, opts?: RequestOptions) =>
    get<StockOpnameDetail>(
      `/stock-opnames/${encodeURIComponent(id)}`,
      {},
      opts
    ),
  /** Lots that can be counted; the ones in another open opname come back `locked`. */
  listOpnameBatches: (q: ListOpnameBatchesQuery, opts?: RequestOptions) =>
    getPage<OpnameBatch>(
      '/stock-opnames/available-batches',
      { ...q, page: q.page ?? 1, limit: q.limit ?? 20 },
      opts
    ),
  /** Dry run of a count sheet (sku, storage_name, batch_number, qty_physical, notes). */
  importStockOpname: (
    ctx: { hubID: number; clientID: number },
    file: File,
    opts?: RequestOptions
  ) => {
    const form = new FormData();
    form.append('file', file);
    form.append('hubID', String(ctx.hubID));
    form.append('clientID', String(ctx.clientID));
    return postMultipart<OpnameImportResult>(
      '/stock-opnames/import',
      form,
      opts
    );
  },
  /** No gap → DONE at once; any gap → waits for the manager. */
  createStockOpname: (body: CreateStockOpnamePayload) =>
    post<StockOpnameDetail>('/stock-opnames', body),
  /** Manager approve: reserves the deficit lines, then waits for FAT. */
  approveStockOpname: (id: string) =>
    post<StockOpnameDetail>(`/stock-opnames/${encodeURIComponent(id)}/approve`),
  rejectStockOpname: (id: string, reason: string) =>
    post<StockOpnameDetail>(`/stock-opnames/${encodeURIComponent(id)}/reject`, {
      reason,
    }),
  /** FAT approve: posts the adjustments to the stock and the ledger. */
  fatApproveStockOpname: (id: string) =>
    post<StockOpnameDetail>(
      `/stock-opnames/${encodeURIComponent(id)}/fat-approve`
    ),
  fatRejectStockOpname: (id: string, reason: string) =>
    post<StockOpnameDetail>(
      `/stock-opnames/${encodeURIComponent(id)}/fat-reject`,
      { reason }
    ),
  /** The requester withdraws it while it waits (manager or FAT); frees the reserve. */
  cancelStockOpname: (id: string, reason: string) =>
    post<StockOpnameDetail>(`/stock-opnames/${encodeURIComponent(id)}/cancel`, {
      reason,
    }),
};

export type WarehouseApi = typeof warehouseApi;
